import Animated from 'react-native-reanimated';
import PlusIcon from '@assets/icons/plus.svg';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlashList as BaseFlashList,
  ContentStyle,
  FlashListProps,
  ListRenderItem,
  ListRenderItemInfo,
  RenderTarget,
} from '@shopify/flash-list';
import { CircledIcon } from './circled-icon';
import { createStyles } from '@/contexts/theme-context';
import {
  InteractionManager,
  PixelRatio,
  Platform,
  ScrollView,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { KeyboardAwareProps, withKeyboardAware } from './with-keyboard-aware';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import {
  ListItem,
  Props as ListItemProps,
  ListItemSeparator,
  ListOption,
  ListOptionValue,
} from './list-item';
import { ListItemHighlight } from './list-item-highlight';
import { OrderGuideCarouselType } from '@/contexts/dynamic-config';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';

const DEFAULT_END_REACHED_THRESHOLD = 0.5;

const fontScale = PixelRatio.getFontScale();
const CREATABLE_HEIGHT = 64 * fontScale;
const SECTION_HEADER_HEIGHT = 24 * fontScale;

export interface ListSection<T, H = undefined> {
  data: T[];
  key: string | null;
  metadata?: H;
}

export enum ListItemType {
  Carousel = 'CAROUSEL',
  Creatable = 'CREATABLE',
  Data = 'DATA',
  ListHeader = 'LIST_HEADER',
  SectionHeader = 'SECTION_HEADER',
}

export type ListCarouselOption<
  TMetadata = {
    renderCarousel: () => React.ReactElement;
    type: OrderGuideCarouselType;
  },
> = ListOption<TMetadata>;

type ListCarousel = Omit<ListCarouselOption, 'value'> & {
  value: ListItemType.Carousel;
};
type ListCreatable = Omit<ListOption, 'value'> & {
  value: ListItemType.Creatable;
};

interface ListHeader {
  value: ListItemType.ListHeader;
}

export type ListSectionHeader<H = undefined> = Omit<ListOption, 'value'> & {
  metadata?: H;
  value: ListItemType.SectionHeader;
};

type ListData<T, H = undefined> =
  | ListCarousel
  | ListCreatable
  | ListHeader
  | ListSectionHeader<H>
  | T;

class FlashList<T> extends BaseFlashList<T> {
  constructor(props: FlashListProps<T>) {
    super(props);

    if (Platform.OS === 'web') {
      // Disable itemAnimator on web so list items don't shift around on load
      // @ts-ignore
      this.itemAnimator = null;
    }
  }
}

const INTERACTION_CHECK_TIMEOUT = 2000;

const getWebScrollViewComponent = (
  skipInteractionCheck?: boolean,
  onVisible?: () => void,
) => {
  // Wrap in forwardRef so scroll methods continue to work
  const WebScrollViewComponent: React.FC<ScrollViewProps> = React.forwardRef(
    (props, ref: React.Ref<ScrollView>) => {
      const [styles] = useStyles();
      const timeoutId = useRef<number | null>(null);

      useEffect(() => {
        return () => {
          if (timeoutId.current) {
            clearTimeout(timeoutId.current);
          }
        };
      }, []);

      const [isVisible, setIsVisible] = useState(false);

      return (
        <ScrollView
          {...props}
          onLayout={(e) => {
            const { layout } = e.nativeEvent;
            const hasNonZeroSize = layout.height !== 0 || layout.width !== 0;

            // @ts-ignore
            const childWidth = e.nativeEvent.target?.firstChild?.clientWidth;

            // Suppress a FlashList warning about unusable rendered size
            // https://github.com/Shopify/flash-list/blob/v1.4.0/src/FlashList.tsx#L422
            layout.height = Math.max(layout.height, 2);
            layout.width = Math.max(
              // FlashList absolutely positions list items based on the width of the
              // list (including any persistent scrollbar) and can cause the scrollbar
              // to overlap the content, so use the width of the child instead
              // @ts-ignore
              typeof childWidth === 'number' ? childWidth : layout.width,
              2,
            );
            props.onLayout?.(e);

            // FlashList v1 has known issues with async layout changes on web.
            // This can cause items to shift around after the initial render.
            // https://shopify.github.io/flash-list/docs/1.x/known-issues#4-web-support-is-in-beta
            //
            // For cases where we want to disregard this, immediately show the list
            if (skipInteractionCheck) {
              setIsVisible(hasNonZeroSize);
              if (hasNonZeroSize) {
                onVisible?.();
              }
              return;
            }

            // InteractionManager cannot differentiate layout changes and other
            // events that could prevent the callback from firing, and its
            // timeout is global. Ensure long network requests do not prevent lists
            // list from displaying by adding a fallback timeout.
            let didSetVisible = false;

            const setVisibleOnce = (visible: boolean) => {
              if (didSetVisible) {
                return;
              }
              didSetVisible = true;
              setIsVisible(visible);
              if (visible) {
                onVisible?.();
              }
            };

            const interactionHandle = InteractionManager.runAfterInteractions(
              () => {
                if (timeoutId.current) {
                  clearTimeout(timeoutId.current);
                }
                setVisibleOnce(hasNonZeroSize);
              },
            );

            timeoutId.current = setTimeout(() => {
              interactionHandle.cancel();
              setVisibleOnce(hasNonZeroSize);
            }, INTERACTION_CHECK_TIMEOUT);
          }}
          ref={ref}
          style={[
            styles.webScrollView,
            isVisible ? styles.webScrollViewVisible : null,
          ]}
        />
      );
    },
  );

  return WebScrollViewComponent;
};

function transformSections<T, H = undefined>(
  sections: ListSection<T, H>[],
): [ListData<T, H>[], number[]] {
  const headerIndices: number[] = [];

  const listData = sections.reduce<ListData<T, H>[]>((result, section) => {
    if (section.key) {
      result.push({
        metadata: section.metadata,
        title: section.key,
        value: ListItemType.SectionHeader,
      } as ListData<T, H>);
      headerIndices.push(result.length - 1);
    }

    return result.concat(section.data);
  }, []);

  return [listData, headerIndices];
}

export interface RecyclerListRef<T> {
  getScrollableNode: FlashList<T>['getScrollableNode'];
  scrollTo: ScrollView['scrollTo'];
  scrollToEnd: FlashList<T>['scrollToEnd'];
  scrollToIndex: FlashList<T>['scrollToIndex'];
  scrollToTop: (options?: { animated?: boolean }) => void;
}

export type Props<T, H = undefined> = Omit<
  FlashListProps<T>,
  | 'contentContainerStyle'
  | 'data'
  | 'estimatedItemSize'
  | 'ListHeaderComponent'
  | 'renderItem'
> & {
  contentContainerStyle?: StyleProp<ViewStyle>;
  // estimatedItemSize can be expressed as a fixed number for all layout sizes
  // or as an array of numbers for small, medium, and large layout sizes. This
  // is used by FlashList to anticipate the size of the list items to improve
  // performance and prevent things from shifting around after the initial draw
  // https://shopify.github.io/flash-list/docs/estimated-item-size
  estimatedItemSize: number | [number, number, number, number];
  FlashListComponent?: typeof FlashList;
  highlightedItemValues?: ListOptionValue[];
  ListHeaderComponent?: React.ReactNode;
  listItemProps?: Partial<ListItemProps<T>>;
  listItemSeparatorProps?: ViewProps;
  listRef?: React.Ref<RecyclerListRef<T>>;
  onSelect?: (item: T) => void;
  onVisible?: () => void;
  renderItem?: ListRenderItem<T>;
  // By default we render ListHeaderComponent as a list item to prevent known
  // bug with sticky header position.
  // - https://github.com/Shopify/flash-list/issues/581
  // But FlashList only calculates item heights once, so changes to the list
  // header height could break the layout.
  // - https://github.com/Shopify/flash-list/issues/581
  // This prop will fall back to default behavior
  renderListHeaderAsListItem?: boolean;
  renderSectionHeader?: (
    item: ListSectionHeader<H>,
    target: RenderTarget,
    index: number,
  ) => React.ReactElement | null;
  scrollViewRef?: React.RefObject<ScrollView | null>;
  sectionHeaderHeight?: number;
  sections: ListSection<T, H>[];
  shouldRenderSeparator?: (leadingItem: T, trailingItem: T) => boolean;
  showSeparator?: boolean;
  showSeparatorBetweenSections?: boolean;
  skipInteractionCheck?: boolean;
};

export const RecyclerList = <T extends ListOption = ListOption, H = undefined>({
  contentContainerStyle,
  estimatedItemSize,
  getItemType,
  FlashListComponent = FlashList,
  highlightedItemValues,
  keyExtractor = (item: T, index: number) =>
    `${item.title}-${item.value}-${index}`,
  ListHeaderComponent,
  listItemProps,
  listItemSeparatorProps,
  listRef,
  onEndReached,
  onEndReachedThreshold = DEFAULT_END_REACHED_THRESHOLD,
  onSelect,
  overrideItemLayout,
  renderItem,
  renderListHeaderAsListItem = true,
  renderSectionHeader,
  scrollViewRef,
  sectionHeaderHeight = SECTION_HEADER_HEIGHT,
  sections,
  shouldRenderSeparator,
  showSeparator,
  showSeparatorBetweenSections,
  skipInteractionCheck,
  onVisible,
  onLoad,
  // Remaining FlashList props
  ...flashListProps
}: Props<T, H>) => {
  const [styles] = useStyles();

  const estimatedItemSizeArray =
    typeof estimatedItemSize === 'number'
      ? [
          estimatedItemSize,
          estimatedItemSize,
          estimatedItemSize,
          estimatedItemSize,
        ]
      : estimatedItemSize;
  const computedEstimatedItemSize = useLayoutSizeValue({
    [LayoutSize.SMALL]: estimatedItemSizeArray[0],
    [LayoutSize.SMALL_TABLET]: estimatedItemSizeArray[1],
    [LayoutSize.MEDIUM]: estimatedItemSizeArray[2],
    [LayoutSize.LARGE]: estimatedItemSizeArray[3],
  });

  const flashListRef = useRef<FlashList<ListData<T, H>> | null>(null);
  const flashListScrollViewRef = useRef<ScrollView>(null);

  // Stable ref to onVisible so it can be used in without specifying in hook deps
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  // Expose functions that require custom behavior to control the FlashList
  useImperativeHandle(listRef, () => ({
    getScrollableNode: () => flashListRef.current?.getScrollableNode() ?? null,
    scrollTo: (options) => flashListScrollViewRef.current?.scrollTo(options),
    scrollToEnd: (options) => {
      // Only the underlying ScrollView has the desired scrollToEnd behavior
      flashListScrollViewRef.current?.scrollToEnd({
        animated: Boolean(options?.animated),
      });
    },
    scrollToIndex: (options) => {
      flashListRef.current?.scrollToIndex({
        ...options,
        // FlashList doesn't automatically take paddingTop into account so it has to be
        // offset manually https://github.com/Shopify/flash-list/issues/702
        // @ts-ignore
        viewOffset: contentContainerStyle?.paddingTop ?? 0,
      });
    },
    scrollToTop: (options) => {
      flashListScrollViewRef.current?.scrollTo?.({
        animated: options?.animated ?? false,
        y: 0,
      });
    },
  }));

  const [listData, headerIndices] = useMemo(() => {
    if (ListHeaderComponent && renderListHeaderAsListItem) {
      // Workaround to stop sticky headers from overlapping ListHeaderComponent
      // by rendering the header in the list content itself
      // https://github.com/Shopify/flash-list/issues/727
      const headerSection: ListSection<ListData<T, H>> = {
        data: [{ value: ListItemType.ListHeader }],
        key: null,
      };

      return transformSections([headerSection, ...sections]);
    }

    return transformSections(sections);
  }, [ListHeaderComponent, sections, renderListHeaderAsListItem]);

  const ScrollComponent = useMemo(
    () =>
      getWebScrollViewComponent(skipInteractionCheck, () =>
        onVisibleRef.current?.(),
      ),
    [skipInteractionCheck],
  );

  const handleLoad = useCallback<
    NonNullable<FlashListProps<ListData<T, H>>['onLoad']>
  >(
    (info) => {
      onLoad?.(info);

      if (Platform.OS !== 'web') {
        onVisibleRef.current?.();
      }
    },
    [onLoad],
  );

  return (
    <FlashListComponent
      // FlashList is overly restrictive about `contentContainerStyle` and doesn't accept
      // an array so the styles have to be flattened
      contentContainerStyle={
        StyleSheet.flatten(contentContainerStyle) as ContentStyle
      }
      data={listData}
      estimatedItemSize={computedEstimatedItemSize}
      getItemType={(item, index) => {
        switch (item.value) {
          case ListItemType.Carousel:
            return ListItemType.Carousel;
          case ListItemType.Creatable:
            return ListItemType.Creatable;
          case ListItemType.ListHeader:
            return ListItemType.ListHeader;
          case ListItemType.SectionHeader:
            return ListItemType.SectionHeader;
          default:
            return getItemType?.(item as T, index);
        }
      }}
      ItemSeparatorComponent={({ leadingItem, trailingItem }) => {
        if (!showSeparator) {
          if (
            leadingItem.value === ListItemType.Carousel ||
            trailingItem.value === ListItemType.Carousel ||
            leadingItem.value === ListItemType.ListHeader ||
            trailingItem.value === ListItemType.ListHeader ||
            leadingItem.value === ListItemType.SectionHeader ||
            (trailingItem.value === ListItemType.SectionHeader &&
              !showSeparatorBetweenSections)
          ) {
            return null;
          }

          if (
            shouldRenderSeparator &&
            !shouldRenderSeparator(leadingItem as T, trailingItem as T)
          ) {
            return null;
          }
        }

        return <ListItemSeparator {...listItemSeparatorProps} />;
      }}
      keyExtractor={(item, index) => {
        switch (item.value) {
          case ListItemType.Carousel:
            return `__${ListItemType.Carousel}__${
              (item as ListCarouselOption).metadata?.type
            }__`;
          case ListItemType.Creatable:
            return `__${ListItemType.Creatable}__`;
          case ListItemType.ListHeader:
            return `__${ListItemType.ListHeader}__`;
          case ListItemType.SectionHeader:
            return `__${ListItemType.SectionHeader}__${item.title}__`;
          default:
            return keyExtractor(item as T, index);
        }
      }}
      ListHeaderComponent={
        ListHeaderComponent && !renderListHeaderAsListItem ? (
          <>{ListHeaderComponent}</>
        ) : undefined
      }
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      onLoad={handleLoad}
      overrideItemLayout={(layout, item, ...rest) => {
        switch (item.value) {
          case ListItemType.Carousel:
            // Carousels should span across all columns
            layout.span = flashListProps.numColumns || 1;
            break;
          case ListItemType.Creatable:
            layout.size = CREATABLE_HEIGHT;
            break;
          case ListItemType.ListHeader:
            // List header should span across all columns
            layout.span = flashListProps.numColumns || 1;
            break;
          case ListItemType.SectionHeader:
            layout.size = sectionHeaderHeight;
            // Section headers should span across all columns
            layout.span = flashListProps.numColumns || 1;
            break;
          default:
            overrideItemLayout?.(layout, item as T, ...rest);
            break;
        }
      }}
      ref={(ref) => {
        flashListRef.current = ref;
        flashListScrollViewRef.current =
          // @ts-ignore _scrollComponent is private to the underlying RecyclerListView
          ref?.recyclerlistview_unsafe._scrollComponent._scrollViewRef;

        if (scrollViewRef && flashListScrollViewRef.current) {
          scrollViewRef.current = flashListScrollViewRef.current;
        }
      }}
      renderItem={(data) => {
        const { item, target, index } = data;
        if (item.value === ListItemType.Creatable) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { renderLeftComponent, renderRightComponent, ...rest } =
            listItemProps || {};
          return (
            <ListItem
              index={index}
              item={item as T}
              onSelect={onSelect}
              renderLeftComponent={() => (
                <CircledIcon
                  Icon={PlusIcon}
                  iconHeight={20}
                  iconWidth={20}
                  size="small"
                />
              )}
              {...rest}
            />
          );
        }

        if (item.value === ListItemType.ListHeader) {
          return <>{ListHeaderComponent}</>;
        }

        if (item.value === ListItemType.SectionHeader) {
          if (typeof renderSectionHeader === 'function') {
            return renderSectionHeader(
              item as ListSectionHeader,
              target,
              index,
            );
          }

          return (
            <View
              onStartShouldSetResponder={() => true}
              style={styles.sectionHeader}
            >
              <Text style={[TYPOGRAPHY.xsmall, styles.sectionHeaderText]}>
                {item.title}
              </Text>
            </View>
          );
        }

        if (item.value === ListItemType.Carousel) {
          const renderCarousel = (item as ListCarouselOption).metadata
            ?.renderCarousel;
          return renderCarousel?.() ?? null;
        }

        if (typeof renderItem === 'function') {
          return renderItem(data as ListRenderItemInfo<T>);
        }

        const { value } = item;

        return (
          <ListItem
            index={index}
            item={item as T}
            onSelect={onSelect}
            renderListItemHeader={
              typeof highlightedItemValues === 'undefined'
                ? listItemProps?.renderListItemHeader
                  ? (option) =>
                      listItemProps.renderListItemHeader?.(option, index)
                  : undefined
                : () => (
                    <ListItemHighlight
                      isVisible={highlightedItemValues.includes(value)}
                    />
                  )
            }
            subtitleMaxLines={1}
            {...listItemProps}
          />
        );
      }}
      renderScrollComponent={
        // WebScrollView prevents FlashList from rendering incorrectly when
        // layout dimensions are unavailable (such as when navigating away)
        Platform.OS === 'web' ? ScrollComponent : undefined
      }
      // Force the scroll indicator to anchor on the right side of the screen
      scrollIndicatorInsets={{ right: 1 }}
      stickyHeaderIndices={headerIndices}
      testID="recycler-scroll-view"
      {...flashListProps}
    />
  );
};

const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);

export const AnimatedRecyclerList = <
  T extends ListOption = ListOption,
  H = undefined,
>(
  props: Props<T, H>,
): React.ReactNode | null => (
  <RecyclerList
    FlashListComponent={AnimatedFlashList as typeof FlashList}
    {...props}
  />
);

export const KeyboardAwareRecyclerList: <T, H = undefined>(
  props: Props<T, H> & KeyboardAwareProps,
) => React.ReactNode | Promise<React.ReactNode> = withKeyboardAware(
  RecyclerList,
  {
    refPropName: 'scrollViewRef',
  },
);

const useStyles = createStyles((theme) => ({
  sectionHeader: {
    backgroundColor: theme.colors.background,
    height: SECTION_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING,
  },
  sectionHeaderText: {
    textTransform: 'uppercase',
  },
  webScrollView: {
    flex: 1,
    opacity: 0,
  },
  webScrollViewVisible: {
    opacity: 1,
  },
}));
